import { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';

const CANAUX = [
  { value: 'POS', label: 'Caisse' },
  { value: 'WEB', label: 'Site web' },
  { value: 'DELIVERY', label: 'Livraison' },
  { value: 'CLICK_AND_COLLECT', label: 'Click & Collect' },
  { value: 'APP', label: 'Application' },
];

const PrixCanalSection = ({ produitId, prixCatalogue }) => {
  const [prixCanaux, setPrixCanaux] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editForm, setEditForm] = useState({ canalVente: '', prix: '', prixPromo: '' });
  const [error, setError] = useState(null);

  const loadPrixCanaux = useCallback(async () => {
    if (!produitId) return;
    setLoading(true);
    try {
      const data = await apiService.getPrixCanal(produitId);
      setPrixCanaux(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erreur chargement prix canal:', err);
    } finally {
      setLoading(false);
    }
  }, [produitId]);

  useEffect(() => {
    loadPrixCanaux();
  }, [loadPrixCanaux]);

  const canauxUtilises = prixCanaux.map((pc) => pc.canalVente);
  const canauxDisponibles = CANAUX.filter((c) => !canauxUtilises.includes(c.value));

  const handleSave = async () => {
    if (!editForm.canalVente || !editForm.prix) return;
    const prix = parseFloat(editForm.prix);
    if (isNaN(prix) || prix < 0) return;

    const prixPromo = editForm.prixPromo ? parseFloat(editForm.prixPromo) : null;
    if (prixPromo !== null && (isNaN(prixPromo) || prixPromo < 0 || prixPromo >= prix)) {
      setError('Le prix promo doit être inférieur au prix');
      return;
    }

    setSaving(editForm.canalVente);
    setError(null);
    try {
      await apiService.upsertPrixCanal(produitId, {
        canalVente: editForm.canalVente,
        prix,
        prixPromo,
      });
      setShowForm(false);
      setEditForm({ canalVente: '', prix: '', prixPromo: '' });
      await loadPrixCanaux();
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
      console.error(err);
    } finally {
      setSaving(null);
    }
  };

  const handleToggleActif = async (pc) => {
    setSaving(pc.canalVente);
    try {
      await apiService.upsertPrixCanal(produitId, {
        canalVente: pc.canalVente,
        prix: pc.prix,
        prixPromo: pc.prixPromo,
        actif: !pc.actif,
      });
      await loadPrixCanaux();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (pc) => {
    if (!confirm(`Supprimer le prix ${getLabelCanal(pc.canalVente)} ?`)) return;
    setSaving(pc.canalVente);
    try {
      await apiService.deletePrixCanal(produitId, pc.id);
      await loadPrixCanaux();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(null);
    }
  };

  const handleEditInline = (pc) => {
    setEditForm({
      canalVente: pc.canalVente,
      prix: String(pc.prix),
      prixPromo: pc.prixPromo != null ? String(pc.prixPromo) : '',
    });
    setShowForm(true);
    setError(null);
  };

  const getLabelCanal = (val) => CANAUX.find((c) => c.value === val)?.label || val;

  if (!produitId) return null;

  return (
    <div className="border-t pt-4 mt-4">
      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
        <span className="text-lg">💰</span>
        Prix par canal de vente
      </h4>
      <p className="text-xs text-gray-500 mb-3">
        Prix catalogue par défaut : <strong>{prixCatalogue != null ? `${Number(prixCatalogue).toFixed(2)} €` : '—'}</strong>.
        Ajoutez un prix spécifique pour un canal, sinon le prix catalogue s'applique.
      </p>

      {loading ? (
        <p className="text-xs text-gray-400">Chargement...</p>
      ) : (
        <>
          {prixCanaux.length > 0 && (
            <div className="overflow-x-auto mb-3">
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Canal</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Prix</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Prix promo</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Actif</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {prixCanaux.map((pc) => (
                    <tr key={pc.id} className={!pc.actif ? 'opacity-50' : ''}>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {getLabelCanal(pc.canalVente)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-sm">
                        {Number(pc.prix).toFixed(2)} €
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-sm text-green-600">
                        {pc.prixPromo != null ? `${Number(pc.prixPromo).toFixed(2)} €` : '—'}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => handleToggleActif(pc)}
                          disabled={saving === pc.canalVente}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                            pc.actif ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                              pc.actif ? 'translate-x-4' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex gap-1 justify-end">
                          <button
                            onClick={() => handleEditInline(pc)}
                            className="text-blue-600 hover:text-blue-800 text-xs px-1.5 py-0.5 rounded hover:bg-blue-50"
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() => handleDelete(pc)}
                            disabled={saving === pc.canalVente}
                            className="text-red-500 hover:text-red-700 text-xs px-1.5 py-0.5 rounded hover:bg-red-50"
                          >
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {prixCanaux.length === 0 && !showForm && (
            <p className="text-xs text-gray-400 italic mb-2">
              Aucun prix canal configuré — le prix catalogue s'applique partout.
            </p>
          )}

          {showForm ? (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Canal</label>
                  {editForm.canalVente && canauxUtilises.includes(editForm.canalVente) ? (
                    <div className="px-3 py-2 bg-gray-100 rounded-lg text-sm">
                      {getLabelCanal(editForm.canalVente)}
                    </div>
                  ) : (
                    <select
                      value={editForm.canalVente}
                      onChange={(e) => setEditForm({ ...editForm, canalVente: e.target.value })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Choisir...</option>
                      {canauxDisponibles.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Prix (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.prix}
                    onChange={(e) => setEditForm({ ...editForm, prix: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Prix promo (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.prixPromo}
                    onChange={(e) => setEditForm({ ...editForm, prixPromo: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                    placeholder="Optionnel"
                  />
                </div>
              </div>
              {error && <p className="text-xs text-red-500">{error}</p>}
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => { setShowForm(false); setEditForm({ canalVente: '', prix: '', prixPromo: '' }); setError(null); }}
                  className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded-lg"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !editForm.canalVente || !editForm.prix}
                  className="px-3 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Sauvegarde...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          ) : (
            canauxDisponibles.length > 0 && (
              <button
                onClick={() => { setShowForm(true); setError(null); }}
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <span className="text-base leading-none">+</span> Ajouter un prix canal
              </button>
            )
          )}
        </>
      )}
    </div>
  );
};

export default PrixCanalSection;
